import React from 'react';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import 'react-quill/dist/quill.snow.css';
import { ErrorMessageInput } from '../error';
import { InputProps } from '@/interfaces';

const ReactQuill = dynamic(() => import('react-quill'), { ssr: false });

interface Props extends InputProps {
	title?: string;
	error?: any;
	className?: string;
	classNameInput?: string;
	markAsRequired?: boolean;
	classNameContainer?: string;
	maxHeight?: number;
	disabled?: boolean;
	value?: string;
	fontSize?: 'sm' | 'base' | 'lg' | 'xl';
	onChange?: (key: string) => void;
}

export const RichEditor: React.FC<Props> = ({
	title,
	error,
	markAsRequired,
	className,
	classNameInput,
	classNameContainer,
	disabled,
	value,
	maxHeight = 500,
	onChange,
	fontSize = 'base',
	...props
}) => {
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = React.useState<number>(200);

	React.useEffect(() => {
		const updateHeight = () => {
			if (wrapperRef.current) {
				const containerHeight = wrapperRef.current.clientHeight;
				const toolbarHeight = 42;
				const newHeight = containerHeight - toolbarHeight;
				setContentHeight(Math.max(200, newHeight));
			}
		};

		const observer = new ResizeObserver(updateHeight);
		if (wrapperRef.current) observer.observe(wrapperRef.current);

		updateHeight();

		return () => observer.disconnect();
	}, []);

	const fontSizeClass = {
		sm: '[&_.ql-editor]:text-sm',
		base: '[&_.ql-editor]:text-base',
		lg: '[&_.ql-editor]:text-base 2xl:[&_.ql-editor]:text-lg',
		xl: '[&_.ql-editor]:text-base 2xl:[&_.ql-editor]:text-xl',
	}[fontSize];

	return (
		<div className={clsx('relative flex flex-col', className)}>
			<div className={clsx('relative group', classNameContainer)}>
				{title && (
					<label className="block text-base text-left mb-2 text-black">
						<span className="relative">
							{title}
							{markAsRequired && (
								<span className="text-main-red absolute -right-3 top-0">*</span>
							)}
						</span>
					</label>
				)}

				<div
					ref={wrapperRef}
					className={clsx(
						'border border-[#0D061633] rounded-[32px] bg-white overflow-hidden',
						'flex flex-col min-h-[200px]',
						{
							'border-main-red': error,
							'max-h-[500px]': !maxHeight,
							[`max-h-[${maxHeight}px]`]: maxHeight,
							'group-hover:bg-[#F4F4F4A3]': disabled,
						}
					)}
					style={{
						resize: 'vertical',
						maxHeight: maxHeight ? `${maxHeight}px` : '500px',
					}}
				>
					<ReactQuill
						value={value}
						readOnly={disabled}
						theme="snow"
						className={clsx(
							'flex flex-col flex-1 w-full text-sm 2xl:text-base font-sans',
							`[&_.ql-editor]:!px-6`,
							'[&_.ql-toolbar]:sticky [&_.ql-toolbar]:top-0 [&_.ql-toolbar]:z-10 [&_.ql-toolbar]:bg-white',
							{
								'placeholder-dark-40 cursor-not-allowed text-opacity-primary bg-transparent':
									disabled,
							},
							fontSizeClass,
							classNameInput
						)}
						style={{
							display: 'flex',
							flexDirection: 'column',
							height: `${contentHeight}px`,
						}}
						modules={{
							toolbar: [['bold', 'italic', 'underline']],
						}}
						onChange={(e) => {
							onChange?.(e);
						}}
						{...props}
					/>
				</div>

				<style jsx global>{`
					.ql-container {
						flex: 1;
						display: flex !important;
						flex-direction: column;
					}
					.ql-editor {
						flex: 1;
						overflow-y: auto !important;
						padding: 16px !important;
					}
					.ql-toolbar {
						position: sticky !important;
						top: 0 !important;
						background: white !important;
						z-index: 10 !important;
						border-bottom: 1px solid #d1d5db !important;
						border-top-left-radius: 32px !important;
						border-top-right-radius: 32px !important;
					}
					.ql-container.ql-snow {
						border: none !important;
						border-bottom-left-radius: 32px !important;
						border-bottom-right-radius: 32px !important;
					}
					.ql-toolbar.ql-snow {
						border: none !important;
						border-bottom: 1px solid #d1d5db !important;
					}
				`}</style>

				{error?.message && <ErrorMessageInput message={error.message} />}
			</div>
		</div>
	);
};
